import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Users, Clock, CheckCircle, XCircle, Plus, ArrowLeft, Sparkles, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Baby, ShieldAlert } from "lucide-react";
import { format, isToday, isBefore, addDays, isSameDay } from "date-fns";
import { PiktableLoader } from "@/components/PiktableLoader";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Table, TableAvailability, TableWeekdayAvailability, CombinedTable } from "@/types/table";
import { isTableAvailableOnDate, isTableBookableOnDate, isTableInTemporaryBlockingPeriod } from "@/lib/tableAvailabilityUtils";
import { getBlockedTableIds } from "@/lib/reservationConflictUtils";
import { findOptimalTable, getOptimalityScore, getTablesInCombinations } from "@/lib/tableAssignmentUtils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link, useNavigate } from "react-router-dom";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { NewReservationDialog } from "@/components/NewReservationDialog";
import { useClientsWithNotes } from "@/hooks/useClientsWithNotes";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { generateAvailableTimes, parseTimeToMinutes, getReservationEndTime } from "@/lib/timeBookingUtils";
import { getEffectiveDuration, getTimeSlotsForDate } from "@/lib/timeSlotUtils";
import { getStaffSlotsForDate } from "@/lib/zoneScheduleUtils";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { useBookableZones } from "@/hooks/useBookableZones";
import { TimeBookingGrid } from "@/components/TimeBookingGrid";
import { ChildrenSelector } from "@/components/ChildrenSelector";
import { Checkbox } from "@/components/ui/checkbox";
import { calculateEffectiveCapacity } from "@/lib/childrenCapacityUtils";
import { useTimeSlotClosures } from "@/hooks/useTimeSlotClosures";
import { useTerraceClosures } from "@/hooks/useTerraceClosures";
import { findParentSlotId } from "@/lib/timeSlotUtils";
import { TerracePreferenceSelector } from "@/components/TerracePreferenceSelector";
import { useTimeSlotLimits, resolveEffectiveLimit } from "@/hooks/useTimeSlotLimits";
import type { TerracePreference } from "@/types/table";

const Availability = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { establishmentId, session } = useAuth();
  const { 
    tables: supabaseTables, 
    combinedTables, 
    reservations: supabaseReservations,
    availabilities: supabaseAvailabilities,
    weekdayAvailabilities: supabaseWeekdayAvailabilities,
    bookablePeriods,
    timeSlots: supabaseTimeSlots,
    dateOverrides,
    loading,
    refetch
  } = useSupabaseData({ useAuthenticatedData: true, establishmentId: establishmentId || undefined, session });
  
  const { settings: reservationSettings, loading: settingsLoading } = useReservationSettings({ 
    establishmentId: establishmentId || undefined 
  });
  const { isTerraceClosedOnDate } = useTerraceClosures({ establishmentId: establishmentId || undefined });
  const { isSlotClosedOnDate } = useTimeSlotClosures({ establishmentId: establishmentId || undefined });
  const { limits: timeSlotLimits } = useTimeSlotLimits({ establishmentId: establishmentId || undefined });
  const { zoneWindows } = useZoneTimeWindows({ establishmentId: establishmentId || undefined, useAuthenticatedData: true });
  const { zones: availabilityZones } = useBookableZones({ establishmentId: establishmentId || undefined, useAuthenticatedData: true });
  const availabilityActiveZoneIds = useMemo(
    () => new Set(availabilityZones.filter(z => z.isActive).map(z => z.id)),
    [availabilityZones]
  );
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  const [numberOfPeople, setNumberOfPeople] = useState<string>("");
  const [showExtendedPeople, setShowExtendedPeople] = useState(false);
  const [selectedTableForReservation, setSelectedTableForReservation] = useState<Table | null>(null);
  const [selectedCombinedTableForReservation, setSelectedCombinedTableForReservation] = useState<CombinedTable | null>(null);
  const [selectedPersonCountForReservation, setSelectedPersonCountForReservation] = useState<number | undefined>(undefined);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [terracePreference, setTerracePreference] = useState<TerracePreference>('no_preference');
  
  // Sorting states
  type SortField = 'name' | 'capacity' | null;
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [retroactiveDialogOpen, setRetroactiveDialogOpen] = useState(false);

  // Correction 1 : exclure les tables "hors réservation" (isBookable = false) — alignement avec la matrice des flux
  const tables = supabaseTables.filter(t => t.isActive && t.isBookable !== false);
  // Tables non-réservables : actives mais isBookable = false — affichées séparément pour réservation manuelle staff
  const unbookableTables = supabaseTables.filter(t => t.isActive && t.isBookable === false);
  const availabilities = supabaseAvailabilities;
  const weekdayAvailabilities = supabaseWeekdayAvailabilities;
  
  // Map time slots from database, filtering by day of week
  const timeSlots = useMemo(() => {
    if (!selectedDate) return [];
    
    const dateStr = formatDateLocal(selectedDate);
    const slotsForDate = getStaffSlotsForDate(selectedDate, supabaseTimeSlots, dateOverrides, zoneWindows, availabilityActiveZoneIds);
    
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    const filtered = hasOverrides
      ? slotsForDate
      : slotsForDate.filter(ts => {
          if (ts.id && isSlotClosedOnDate(ts.id, dateStr)) return false;
          return true;
        });
    
    return filtered.map(ts => ({
      start: ts.start.substring(0, 5),
      end: ts.end.substring(0, 5),
      label: `${ts.start.substring(0, 5)} - ${ts.end.substring(0, 5)}`
    }));
  }, [supabaseTimeSlots, dateOverrides, selectedDate, isSlotClosedOnDate, zoneWindows, availabilityActiveZoneIds]);

  // Detect if we're in TIME mode
  const isTimeMode = reservationSettings?.bookingMode === 'time';
  const reservationDurationMinutes = reservationSettings?.reservationDurationMinutes ?? 120;
  
  // Children/stroller settings
  const childrenEnabled = reservationSettings?.childrenEnabled ?? false;
  const strollerEnabled = reservationSettings?.strollerEnabled ?? false;
  const childrenCapacityMode = reservationSettings?.childrenCapacityMode ?? 'full';
  const hasChildren = childrenEnabled && numberOfChildren > 0;
  // Correction 2 : inclure strollerEnabled — alignement avec Book.tsx et ModifyReservation.tsx
  const strollerActive = hasStroller && strollerEnabled;

  // Clamp numberOfChildren when numberOfPeople decreases
  useEffect(() => {
    if (numberOfPeople) {
      const maxChildren = parseInt(numberOfPeople) - 1;
      if (numberOfChildren > maxChildren) {
        setNumberOfChildren(Math.max(0, maxChildren));
      }
    } else {
      setNumberOfChildren(0);
    }
  }, [numberOfPeople]);


  // Generate individual booking times for TIME mode
  const availableBookingTimes = useMemo(() => {
    if (!isTimeMode || !selectedDate || timeSlots.length === 0) return [];
    
    // Get booking interval from settings (default 30 min if not properly set)
    const intervalSetting = (reservationSettings as any)?.bookingIntervalMinutes;
    const interval = [15, 30, 60].includes(intervalSetting) ? intervalSetting as 15 | 30 | 60 : 30;
    
    const slotRanges = timeSlots.map(ts => ({ start: ts.start, end: ts.end, durationOverrideMinutes: (supabaseTimeSlots.find(s => s.start.substring(0, 5) === ts.start && s.end.substring(0, 5) === ts.end) as any)?.durationOverrideMinutes }));
    return generateAvailableTimes(slotRanges, interval, reservationDurationMinutes);
  }, [isTimeMode, selectedDate, timeSlots, reservationSettings, reservationDurationMinutes, supabaseTimeSlots]);

  // Get existing clients from reservation data with notes
  const { clients: existingClients } = useClientsWithNotes({ 
    reservations: supabaseReservations 
  });

  // Créer les options de conflit basées sur le mode de réservation
  const bookingMode = reservationSettings?.bookingMode as 'slot' | 'time' | undefined;
  const globalDuration = reservationSettings?.reservationDurationMinutes ?? 120;

  // Default conflict options (uses global duration — used for non-slot-specific calls)
  const conflictOptions = useMemo(() => {
    if (!reservationSettings) return undefined;
    return {
      bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
      reservationDurationMinutes: globalDuration,
      serviceSlots: selectedDate ? getTimeSlotsForDate(selectedDate, supabaseTimeSlots, dateOverrides) : supabaseTimeSlots,
    };
  }, [reservationSettings, globalDuration, selectedDate, supabaseTimeSlots, dateOverrides]);

  // Build conflict options with per-slot effective duration
  const resolvedSlotsForDate = useMemo(() => {
    if (!selectedDate) return supabaseTimeSlots;
    return getTimeSlotsForDate(selectedDate, supabaseTimeSlots, dateOverrides);
  }, [selectedDate, supabaseTimeSlots, dateOverrides]);

  const getSlotConflictOptions = useCallback((time: string) => {
    if (!bookingMode || bookingMode === 'slot') return conflictOptions;
    const effectiveDuration = getEffectiveDuration(time, resolvedSlotsForDate, globalDuration);
    return {
      bookingMode: 'time' as const,
      reservationDurationMinutes: effectiveDuration
    };
  }, [bookingMode, resolvedSlotsForDate, globalDuration, conflictOptions]);

  // Calculate availability for each time slot when date is selected
  // Note: This calculation is independent of numberOfPeople for maxCapacity
  const timeSlotAvailability = useMemo(() => {
    if (!selectedDate) return {};

    const result: Record<string, { 
      available: number; 
      totalCapacity: number; 
      maxCapacity: number;
      tables: Table[]; 
      combinedTables: CombinedTable[];
    }> = {};

    const dateStr = formatDateLocal(selectedDate);

    timeSlots.forEach(slot => {
      // Use centralized function that automatically filters out cancellations
      const blockedIds = getBlockedTableIds(
        supabaseReservations,
        combinedTables,
        selectedDate,
        slot.start,
        getSlotConflictOptions(slot.start)
      );

      // Check terrace closure for this specific slot
      const slotId = findParentSlotId(slot.start, supabaseTimeSlots);
      const terraceClosedForSlot = isTerraceClosedOnDate(dateStr, slotId);

      // Get ALL available tables (without filtering by numberOfPeople for maxCapacity calculation)
      const availableTables = tables.filter(table => {
        // Check if table is blocked by a reservation
        if (blockedIds.has(table.id)) return false;
        
        // Check if table is available on the selected date
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), weekdayAvailability || null)) {
          return false;
        }

        // Children/stroller filtering
        if (hasChildren && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;

        // Terrace closure filtering
        if (terraceClosedForSlot && table.isTerrace) return false;

        // Terrace preference filtering
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;

        return true;
      });

      // Tables combinées disponibles (without filtering by numberOfPeople)
      const availableCombinedTables = combinedTables.filter(ct => {
        if (!ct.isActive) return false;
        // Correction 3 : filtrer isBookable sur les combinées — alignement avec Book.tsx
        if (ct.isBookable === false) return false;
        if (blockedIds.has(ct.id)) return false;
        if (ct.tableIds.some(tid => blockedIds.has(tid))) return false;
        
        // Terrace closure filtering
        if (terraceClosedForSlot && ct.isTerrace) return false;

        // Terrace preference filtering
        if (terracePreference === 'terrace' && !ct.isTerrace) return false;
        if (terracePreference === 'indoor' && ct.isTerrace) return false;
        
        // Children/stroller filtering on combined table itself
        if (hasChildren && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;

        // Correction 4 : vérifier allows_children/allows_stroller sur chaque sous-table
        if (hasChildren) {
          const allAllowChildren = ct.tableIds.every(tid => {
            const t = supabaseTables.find(tb => tb.id === tid);
            return t && t.allowsChildren !== false;
          });
          if (!allAllowChildren) return false;
        }
        if (strollerActive && ct.strollerPolicy !== 'allow') {
          const allAllowStroller = ct.tableIds.every(tid => {
            const t = supabaseTables.find(tb => tb.id === tid);
            return t && t.allowsStroller !== false;
          });
          if (!allAllowStroller) return false;
        }

        // Correction 5 : vérifier les périodes temporaires des sous-tables — alignement avec Book.tsx
        const noTemporaryBlocking = ct.tableIds.every(tid => {
          return !isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods);
        });
        if (!noTemporaryBlocking) return false;
        
        const allTablesAvailableOnDate = ct.tableIds.every(tid => {
          const table = supabaseTables.find(t => t.id === tid);
          if (!table) return false;
          const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(
            table,
            selectedDate,
            availabilities.filter(a => !a.isAvailable),
            weekdayAvailability || null
          );
        });
        
        if (!allTablesAvailableOnDate) return false;
        
        return true;
      });

      // Tables HR/manuelles physiquement disponibles (manager uniquement, SLOT mode)
      const availableManualTables = unbookableTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), weekdayAvailability || null)) return false;
        if (hasChildren && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (terraceClosedForSlot && table.isTerrace) return false;
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;
        return true;
      });

      // Compter les trois types (bookable + combinées + manuelles)
      const totalAvailable = availableTables.length + availableCombinedTables.length + availableManualTables.length;
      const totalCapacitySum = 
        availableTables.reduce((sum, t) => sum + t.capacity, 0) +
        availableCombinedTables.reduce((sum, ct) => sum + ct.totalCapacity, 0) +
        availableManualTables.reduce((sum, t) => sum + t.capacity, 0);

      // Calculate maxCapacity: the maximum number of people a single table/combination can accommodate
      const maxSingleCapacity = availableTables.length > 0 
        ? Math.max(...availableTables.map(t => t.capacity)) 
        : 0;
      const maxCombinedCapacity = availableCombinedTables.length > 0
        ? Math.max(...availableCombinedTables.map(ct => ct.totalCapacity))
        : 0;
      const maxManualCapacity = availableManualTables.length > 0
        ? Math.max(...availableManualTables.map(t => t.capacity))
        : 0;
      const maxCapacity = Math.max(maxSingleCapacity, maxCombinedCapacity, maxManualCapacity);

      result[slot.start] = {
        available: totalAvailable,
        totalCapacity: totalCapacitySum,
        maxCapacity,
        tables: availableTables,
        combinedTables: availableCombinedTables,
      };
    });

    return result;
  }, [selectedDate, tables, supabaseReservations, combinedTables, availabilities, weekdayAvailabilities, timeSlots, getSlotConflictOptions, hasChildren, strollerActive, isTerraceClosedOnDate, supabaseTimeSlots, terracePreference, unbookableTables, bookablePeriods]);

  // Calculate TIME mode availability for each individual time
  const timeAvailability = useMemo(() => {
    if (!isTimeMode || !selectedDate || availableBookingTimes.length === 0) return [];

    const now = new Date();
    const personsNum = numberOfPeople ? parseInt(numberOfPeople) : 0;
    const dateStr = formatDateLocal(selectedDate);

    return availableBookingTimes.map(time => {
      // Check if time is in the past for today
      if (isToday(selectedDate)) {
        const [hours, minutes] = time.split(':').map(Number);
        const bookingTime = new Date(selectedDate);
        bookingTime.setHours(hours, minutes, 0, 0);
        if (isBefore(bookingTime, now)) {
          return { time, isAvailable: false, unavailableReason: 'hours' as const };
        }
      }

      // Get blocked table IDs for this specific time
      const blockedIds = getBlockedTableIds(
        supabaseReservations,
        combinedTables,
        selectedDate,
        time,
        getSlotConflictOptions(time)
      );

      // Check terrace closure for this specific time
      const slotId = findParentSlotId(time, supabaseTimeSlots);
      const terraceClosedForSlot = isTerraceClosedOnDate(dateStr, slotId);

      // Tables simples disponibles pour cette heure
      const availableSingleTables = tables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), weekdayAvailability || null)) return false;
        // Staff bypass: no capacity filtering for time slots
        if (hasChildren && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (terraceClosedForSlot && table.isTerrace) return false;
        // Terrace preference filtering
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;
        return true;
      });

      // Combinaisons disponibles pour cette heure
      const availableCombined = combinedTables.filter(ct => {
        if (blockedIds.has(ct.id) || !ct.isActive) return false;
        if (ct.isBookable === false) return false;
        // Staff bypass: no capacity filtering for combined tables
        if (ct.tableIds.some(tid => blockedIds.has(tid))) return false;
        if (hasChildren && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        if (terraceClosedForSlot && ct.isTerrace) return false;
        // Terrace preference filtering
        if (terracePreference === 'terrace' && !ct.isTerrace) return false;
        if (terracePreference === 'indoor' && ct.isTerrace) return false;

        // Children/stroller checks on sub-tables
        if (hasChildren) {
          const allAllowChildren = ct.tableIds.every(tid => {
            const t = supabaseTables.find(tb => tb.id === tid);
            return t && t.allowsChildren !== false;
          });
          if (!allAllowChildren) return false;
        }
        if (strollerActive && ct.strollerPolicy !== 'allow') {
          const allAllowStroller = ct.tableIds.every(tid => {
            const t = supabaseTables.find(tb => tb.id === tid);
            return t && t.allowsStroller !== false;
          });
          if (!allAllowStroller) return false;
        }

        // Bookable periods on sub-tables
        const noTemporaryBlocking = ct.tableIds.every(tid => {
          return !isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods);
        });
        if (!noTemporaryBlocking) return false;

        return ct.tableIds.every(tid => {
          const table = supabaseTables.find(t => t.id === tid);
          if (!table) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), weekdayAvail || null);
        });
      });

      // Tables HR/manuelles physiquement disponibles (manager uniquement)
      // Ne vérifie PAS isBookable — ces tables sont réservables manuellement par le staff
      const availableManualTables = unbookableTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), weekdayAvailability || null)) return false;
        if (hasChildren && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (terraceClosedForSlot && table.isTerrace) return false;
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;
        return true;
      });

      const isAvailable = availableSingleTables.length > 0 || availableCombined.length > 0 || availableManualTables.length > 0;
      
      return {
        time,
        isAvailable,
        unavailableReason: isAvailable ? undefined : 'full' as const
      };
    });
  }, [isTimeMode, selectedDate, availableBookingTimes, numberOfPeople, supabaseReservations, tables, combinedTables, availabilities, weekdayAvailabilities, getSlotConflictOptions, hasChildren, strollerActive, isTerraceClosedOnDate, supabaseTimeSlots, bookablePeriods, terracePreference, unbookableTables]);

  // Staff bypass: fixed 1-20 range (staff can book beyond table capacity)
  const availablePersonCounts = useMemo(() => {
    if (!selectedDate) return [];

    const minPeople = reservationSettings?.minPeople ?? 1;
    const staffMaxPeople = 20; // Staff bypass: always 1-20

    const results: Array<{ count: number; isAvailable: boolean }> = [];
    for (let count = minPeople; count <= staffMaxPeople; count++) {
      results.push({ count, isAvailable: true });
    }

    return results;
  }, [selectedDate, reservationSettings]);

  // Reset numberOfPeople if current selection becomes invalid
  useEffect(() => {
    if (numberOfPeople && availablePersonCounts.length > 0) {
      const currentPersons = parseInt(numberOfPeople);
      const isStillAvailable = availablePersonCounts.some(
        p => p.count === currentPersons && p.isAvailable
      );
      if (!isStillAvailable) {
        setNumberOfPeople("");
      }
    }
  }, [availablePersonCounts, numberOfPeople]);

  // Calculate detailed results when all criteria are selected
  const searchResults = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot) {
      return { 
        available: [], 
        unavailable: [], 
        availableCombined: [],
        unavailableCombined: [],
        manualOnly: [] as { table: Table; reason: string | null }[],
        manualOnlyCombined: [] as { combinedTable: CombinedTable; reason: string | null }[],
        totalCapacity: 0 
      };
    }

    const personsNum = numberOfPeople ? parseInt(numberOfPeople) : 0;

    // Correction 2 : utiliser effectiveCapacityNeeded au lieu de personsNum brut pour le filtrage de capacité
    const adultsNumSearch = childrenEnabled ? Math.max(0, personsNum - numberOfChildren) : personsNum;
    const effectiveCapacityNeeded = hasChildren
      ? calculateEffectiveCapacity(adultsNumSearch, numberOfChildren, childrenCapacityMode as 'full' | 'half' | 'none')
      : personsNum;

    const available: Table[] = [];
    const unavailable: { table: Table; reason: string }[] = [];
    const availableCombined: CombinedTable[] = [];
    const unavailableCombined: { combinedTable: CombinedTable; reason: string }[] = [];

    // Use centralized function that automatically filters out cancellations
    const blockedIds = getBlockedTableIds(
      supabaseReservations,
      combinedTables,
      selectedDate,
      selectedTimeSlot,
      getSlotConflictOptions(selectedTimeSlot)
    );
    // Check terrace closure for this slot
    const dateStr = formatDateLocal(selectedDate);
    const slotId = findParentSlotId(selectedTimeSlot, supabaseTimeSlots);
    const terraceClosedForSlot = isTerraceClosedOnDate(dateStr, slotId);

    // Process simple tables
    tables.forEach(table => {
      const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
      const isAvailableOnDate = isTableAvailableOnDate(
        table,
        selectedDate,
        availabilities.filter(a => !a.isAvailable),
        weekdayAvailability || null
      );

      // Absolute blockers (shown alone)
      if (!isAvailableOnDate) {
        unavailable.push({ table, reason: "Non disponible à cette date" });
        return;
      }
      if (!isTableBookableOnDate(table, selectedDate, bookablePeriods)) {
        unavailable.push({ table, reason: "Table temporairement non réservable" });
        return;
      }
      if (blockedIds.has(table.id)) {
        unavailable.push({ table, reason: "Déjà réservée" });
        return;
      }

      // Accumulate constraint reasons
      const reasons: string[] = [];
      if (terraceClosedForSlot && table.isTerrace) reasons.push("Terrasse fermée");
      if (terracePreference === 'terrace' && !table.isTerrace) reasons.push("Table en intérieur");
      if (terracePreference === 'indoor' && table.isTerrace) reasons.push("Table en terrasse");
      if (hasChildren && table.allowsChildren === false) reasons.push("N'accepte pas les enfants");
      if (strollerActive && table.allowsStroller === false) reasons.push("N'accepte pas les poussettes");

      if (reasons.length > 0) {
        unavailable.push({ table, reason: reasons.join(" · ") });
        return;
      }

      available.push(table);
    });

    // Process combined tables
    const manualOnlyCombined: { combinedTable: CombinedTable; reason: string | null }[] = [];

    combinedTables.forEach(ct => {
      // Absolute blockers (shown alone)
      if (!ct.isActive) {
        unavailableCombined.push({ combinedTable: ct, reason: "Table combinée inactive" });
        return;
      }

      // Combined tables marked "hors réservation" → handled like manual-only simple tables
      // GARANTIE ANTI-RÉGRESSION : on calcule TOUTES les raisons d'abord, puis on push UNE SEULE FOIS
      // → impossible qu'une combinée non-réservable disparaisse silencieusement.
      if (ct.isBookable === false) {
        const reasons: string[] = [];

        // Sous-tables introuvables (table supprimée mais lien combinée resté)
        const missingSubTables = ct.tableIds.filter(tid => !supabaseTables.find(t => t.id === tid));
        if (missingSubTables.length > 0) {
          reasons.push("Sous-table(s) introuvable(s)");
        }

        // Sous-table indisponible à la date (jour de semaine, blocage permanent)
        const subUnavailable = ct.tableIds.some(tid => {
          const table = supabaseTables.find(t => t.id === tid);
          if (!table) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return !isTableAvailableOnDate(
            table,
            selectedDate,
            availabilities.filter(a => !a.isAvailable),
            weekdayAvail || null
          );
        });
        if (subUnavailable) reasons.push("Une sous-table indisponible à cette date");

        // Blocage temporaire sur une sous-table
        const subTempBlocked = ct.tableIds.some(tid =>
          isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods)
        );
        if (subTempBlocked) reasons.push("Une sous-table temporairement non réservable");

        // Conflit de réservation (combinée elle-même ou une sous-table)
        if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) {
          reasons.push("Déjà réservée");
        }

        // Terrasse fermée
        if (terraceClosedForSlot && ct.isTerrace) {
          reasons.push("Terrasse fermée");
        }

        manualOnlyCombined.push({
          combinedTable: ct,
          reason: reasons.length > 0 ? reasons.join(" · ") : null,
        });
        return;
      }

      if (blockedIds.has(ct.id)) {
        unavailableCombined.push({ combinedTable: ct, reason: "Déjà réservée" });
        return;
      }

      // Check sub-tables availability (absolute blocker)
      const allTablesAvailable = ct.tableIds.every(tid => {
        const table = supabaseTables.find(t => t.id === tid);
        if (!table) return false;
        if (blockedIds.has(tid)) return false;
        const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
        return isTableAvailableOnDate(
          table,
          selectedDate,
          availabilities.filter(a => !a.isAvailable),
          weekdayAvail || null
        );
      });
      if (!allTablesAvailable) {
        unavailableCombined.push({ combinedTable: ct, reason: "Une ou plusieurs tables composantes indisponibles" });
        return;
      }

      const noTemporaryBlocking = ct.tableIds.every(tid => {
        return !isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods);
      });
      if (!noTemporaryBlocking) {
        unavailableCombined.push({ combinedTable: ct, reason: "Une sous-table temporairement non réservable" });
        return;
      }

      // Accumulate constraint reasons
      const reasons: string[] = [];
      if (terraceClosedForSlot && ct.isTerrace) reasons.push("Terrasse fermée");
      if (terracePreference === 'terrace' && !ct.isTerrace) reasons.push("Table combinée en intérieur");
      if (terracePreference === 'indoor' && ct.isTerrace) reasons.push("Table combinée en terrasse");
      if (hasChildren && ct.allowsChildren === false) reasons.push("N'accepte pas les enfants");
      if (strollerActive && ct.allowsStroller === false) reasons.push("N'accepte pas les poussettes");

      // Sub-table children/stroller checks
      if (hasChildren) {
        const allAllowChildren = ct.tableIds.every(tid => {
          const t = supabaseTables.find(tb => tb.id === tid);
          return t && t.allowsChildren !== false;
        });
        if (!allAllowChildren && !reasons.includes("N'accepte pas les enfants")) {
          reasons.push("Une sous-table n'accepte pas les enfants");
        }
      }
      if (strollerActive && ct.strollerPolicy !== 'allow') {
        const allAllowStroller = ct.tableIds.every(tid => {
          const t = supabaseTables.find(tb => tb.id === tid);
          return t && t.allowsStroller !== false;
        });
        if (!allAllowStroller && !reasons.includes("N'accepte pas les poussettes")) {
          reasons.push("Une sous-table n'accepte pas les poussettes");
        }
      }

      if (reasons.length > 0) {
        unavailableCombined.push({ combinedTable: ct, reason: reasons.join(" · ") });
        return;
      }

      availableCombined.push(ct);
    });

    const totalCapacity = available.reduce((sum, t) => sum + t.capacity, 0) +
                          availableCombined.reduce((sum, ct) => sum + ct.totalCapacity, 0);

    // Process non-bookable tables (manual-only for staff)
    const manualOnly: { table: Table; reason: string | null }[] = [];
    unbookableTables.forEach(table => {
      const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
      const isAvailableOnDate = isTableAvailableOnDate(
        table,
        selectedDate,
        availabilities.filter(a => !a.isAvailable),
        weekdayAvailability || null
      );

      if (!isAvailableOnDate) {
        manualOnly.push({ table, reason: "Non disponible à cette date" });
        return;
      }
      if (blockedIds.has(table.id)) {
        manualOnly.push({ table, reason: "Déjà réservée" });
        return;
      }
      if (terraceClosedForSlot && table.isTerrace) {
        manualOnly.push({ table, reason: "Terrasse fermée" });
        return;
      }

      manualOnly.push({ table, reason: null }); // physically available
    });

    return { available, unavailable, availableCombined, unavailableCombined, manualOnly, manualOnlyCombined, totalCapacity };
  }, [selectedDate, selectedTimeSlot, numberOfPeople, tables, unbookableTables, supabaseReservations, combinedTables, availabilities, weekdayAvailabilities, getSlotConflictOptions, hasChildren, strollerActive, isTerraceClosedOnDate, supabaseTimeSlots, bookablePeriods, terracePreference, supabaseTables]);

  // Calcul de la suggestion optimale
  const optimalSuggestion = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot || !numberOfPeople) return null;
    
    const personsNum = parseInt(numberOfPeople);
    // Correction 3 : passer adultsNum (adultes seulement) à findOptimalTable
    // findOptimalTable recalcule lui-même effectiveCapacity = adultes + impact(enfants selon mode)
    // Si on passe le total, le calcul devient total + impact(enfants), ce qui sur-estime la capacité requise
    const adultsNum = childrenEnabled ? Math.max(1, personsNum - numberOfChildren) : personsNum;
    return findOptimalTable(
      adultsNum,
      selectedDate,
      selectedTimeSlot,
      tables,
      combinedTables,
      supabaseReservations,
      availabilities,
      weekdayAvailabilities,
      getSlotConflictOptions(selectedTimeSlot),
      bookablePeriods,
      childrenEnabled ? numberOfChildren : 0,
      childrenCapacityMode,
      hasStroller,
      undefined,
      undefined,
      reservationSettings?.customAssignmentOrderEnabled ?? false
    );
  }, [selectedDate, selectedTimeSlot, numberOfPeople, tables, combinedTables, supabaseReservations, availabilities, weekdayAvailabilities, getSlotConflictOptions, bookablePeriods, numberOfChildren, childrenEnabled, childrenCapacityMode, hasStroller, reservationSettings?.customAssignmentOrderEnabled]);

  // Resolve effective limit for the selected day/slot (informational for staff)
  const effectiveLimit = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot || !reservationSettings) return null;
    const dayOfWeek = selectedDate.getDay();
    const slotId = findParentSlotId(selectedTimeSlot, supabaseTimeSlots);
    return resolveEffectiveLimit(
      dayOfWeek,
      slotId,
      timeSlotLimits,
      {
        timeLimitType: reservationSettings.timeLimitType ?? 'none',
        maxReservationsPerTime: reservationSettings.maxReservationsPerTime ?? null,
        maxPeoplePerTime: reservationSettings.maxPeoplePerTime ?? null,
      },
      selectedTimeSlot
    );
  }, [selectedDate, selectedTimeSlot, reservationSettings, supabaseTimeSlots, timeSlotLimits]);

  const naturalCompare = (a: string, b: string): number => {
    return a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' });
  };

  // Sorted available tables
  const sortedAvailable = useMemo(() => {
    if (!sortField) return searchResults.available;
    
    return [...searchResults.available].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = naturalCompare(a.name, b.name);
      } else if (sortField === 'capacity') {
        comparison = a.capacity - b.capacity;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [searchResults.available, sortField, sortDirection]);

  // Sorted available combined tables
  const sortedAvailableCombined = useMemo(() => {
    if (!sortField) return searchResults.availableCombined;
    
    return [...searchResults.availableCombined].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = naturalCompare(a.name, b.name);
      } else if (sortField === 'capacity') {
        comparison = a.totalCapacity - b.totalCapacity;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [searchResults.availableCombined, sortField, sortDirection]);

  const handleReset = () => {
    setSelectedDate(undefined);
    setSelectedTimeSlot("");
    setNumberOfPeople("");
    setNumberOfChildren(0);
    setHasStroller(false);
    setTerracePreference('no_preference');
  };

  const showResults = selectedDate && selectedTimeSlot;

  const handleCreateReservation = async (newReservation: {
    customerFirstName: string;
    customerLastName: string;
    customerEmail?: string;
    customerPhone?: string;
    numberOfPeople: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    tableId?: string;
    combinedTableId?: string;
    startTime: string;
    date: Date;
    notes?: string;
    dietaryPreferences?: string[];
    internalNotes?: string;
    durationMinutes?: number;
    source?: string;
    terracePreference?: string;
    zonePreference?: string[];
    status?: string;
    isRetroactive?: boolean;
    skipEmails?: boolean;
    skipTokens?: boolean;
    forceOverride?: boolean;
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-reservation', {
        body: {
          establishmentId,
          customer: {
            email: newReservation.customerEmail || null,
            firstName: newReservation.customerFirstName,
            lastName: newReservation.customerLastName,
            phone: newReservation.customerPhone || null,
          },
          reservation: {
            date: formatDateLocal(newReservation.date),
            startTime: newReservation.startTime,
            tableId: newReservation.tableId || null,
            combinedTableId: newReservation.combinedTableId || null,
            numberOfPeople: newReservation.numberOfPeople,
            // Correction 4 : transmettre les champs manquants — alignement sur Dashboard.tsx
            numberOfChildren: newReservation.numberOfChildren || 0,
            hasStroller: newReservation.hasStroller ?? false,
            terracePreference: newReservation.terracePreference || 'no_preference',
            zonePreference: Array.isArray(newReservation.zonePreference) && newReservation.zonePreference.length > 0 ? newReservation.zonePreference : undefined,
            notes: newReservation.notes,
            dietaryPreferences: newReservation.dietaryPreferences,
            internalNotes: newReservation.internalNotes || null,
            durationMinutes: newReservation.durationMinutes || null,
            source: newReservation.source || null,
            status: newReservation.status || null,
          },
          skipEmails: newReservation.skipEmails ?? false,
          skipTokens: newReservation.skipTokens ?? false,
          forceOverride: newReservation.forceOverride ?? false,
        }
      });

      if (error) throw error;

      toast({
        title: "Réservation créée",
        description: `Réservation pour ${[newReservation.customerFirstName, newReservation.customerLastName].filter(Boolean).join(' ') || 'Client'} créée avec succès.`,
      });
      
      // Rafraîchir les données
      await refetch();
      
      // Close dialog and navigate back
      setSelectedTableForReservation(null);
      navigate("/dashboard");
    } catch (error) {
      console.error('Erreur création réservation:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer la réservation.",
        variant: "destructive",
      });
    }
  };

  if (loading || settingsLoading || !reservationSettings) {
    return <PiktableLoader message="Chargement des disponibilités..." />;
  }

  return (
    <div className="min-h-screen py-4 sm:py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur pb-4 sm:pb-6 mb-4 sm:mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="mb-2 sm:mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Retour au tableau de bord</span>
              <span className="sm:hidden">Retour</span>
            </Button>
          </Link>
          <div className="flex items-start justify-between gap-3 mb-1 sm:mb-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-4xl font-serif font-bold">Recherche de disponibilités</h1>
              {reservationSettings && (
                <Badge variant="outline" className="text-xs font-normal">
                  {isTimeMode 
                    ? `Mode horaires • ${reservationDurationMinutes} min`
                    : 'Mode créneaux'}
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRetroactiveDialogOpen(true)}
              className="shrink-0"
              title="Enregistrer une réservation déjà passée (aucun email envoyé)"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Réservation passée</span>
              <span className="sm:hidden">Passée</span>
            </Button>
          </div>
          <p className="text-muted-foreground text-sm sm:text-base hidden sm:block">
            Trouvez les tables disponibles en temps réel
          </p>
          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Attention, lorsqu'une confirmation est effectuée depuis ici, un mail de confirmation est automatiquement envoyé au client.
            </AlertDescription>
          </Alert>
        </div>

        {/* Search Form */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Critères de recherche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Date Selection */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">1. Choisissez une date *</Label>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const quickDates = Array.from({ length: 8 }, (_, i) => addDays(today, i));
                    const isQuickDate = selectedDate && quickDates.some(d => isSameDay(d, selectedDate));
                    
                    return (
                      <>
                        {quickDates.map((date, i) => (
                          <Button
                            key={i}
                            variant={selectedDate && isSameDay(selectedDate, date) ? "default" : "outline"}
                            className="flex flex-col items-center h-auto py-2 px-3 min-w-[3.5rem]"
                            onClick={() => {
                              setSelectedDate(date);
                              setIsCalendarOpen(false);
                            }}
                          >
                            <span className="text-[10px] uppercase leading-tight">
                              {i === 0 ? "Auj." : format(date, "EEE", { locale: fr })}
                            </span>
                            <span className="text-sm font-semibold">{format(date, "d")}</span>
                          </Button>
                        ))}
                        
                        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant={selectedDate && !isQuickDate ? "default" : "outline"}
                              className="flex items-center gap-1 h-auto py-2 px-3"
                            >
                              <CalendarIcon className="h-4 w-4" />
                              <span className="text-xs">Autre date</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={selectedDate}
                              onSelect={(date) => {
                                setSelectedDate(date);
                                setIsCalendarOpen(false);
                              }}
                              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                              initialFocus
                              locale={fr}
                              className="p-3 pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                        
                        {selectedDate && !isQuickDate && (
                          <span className="flex items-center text-sm text-muted-foreground ml-1">
                            {format(selectedDate, "PPP", { locale: fr })}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Number of People - Optional */}
              {selectedDate && availablePersonCounts.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-base font-semibold">
                    2. Nombre de personnes
                  </Label>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={numberOfPeople === "" ? "default" : "outline"}
                      onClick={() => setNumberOfPeople("")}
                      className="h-14 sm:h-12 px-4 text-sm sm:text-base active:scale-95 transition-transform"
                    >
                      Tous
                    </Button>
                    {availablePersonCounts
                      .filter(({ isAvailable }) => isAvailable)
                      .filter(({ count }) => showExtendedPeople || (numberOfPeople && parseInt(numberOfPeople) > 8) || count <= 8)
                      .map(({ count }) => (
                        <Button
                          key={count}
                          variant={numberOfPeople === count.toString() ? "default" : "outline"}
                          onClick={() => setNumberOfPeople(count.toString())}
                          className="h-14 sm:h-12 w-14 sm:w-12 text-sm sm:text-base active:scale-95 transition-transform"
                        >
                          {count}
                        </Button>
                      ))}
                    {!showExtendedPeople && !(numberOfPeople && parseInt(numberOfPeople) > 8) && (
                      <Button
                        variant="outline"
                        onClick={() => setShowExtendedPeople(true)}
                        className="h-14 sm:h-12 px-3 text-sm sm:text-base active:scale-95 transition-transform"
                      >
                        9+ ▸
                      </Button>
                    )}
                    {(showExtendedPeople || (numberOfPeople && parseInt(numberOfPeople) > 8)) && (
                      <Button
                        variant="ghost"
                        onClick={() => setShowExtendedPeople(false)}
                        className="h-14 sm:h-12 px-3 text-sm sm:text-base active:scale-95 transition-transform"
                      >
                        ◂
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    {selectedTimeSlot 
                      ? "Les capacités affichées correspondent à ce créneau"
                      : "Les capacités affichées sont disponibles sur au moins un créneau"}
                  </p>
                </div>
              )}

              {/* Children Selector - conditionally shown */}
              {childrenEnabled && numberOfPeople && parseInt(numberOfPeople) > 1 && (
                <div className="space-y-3">
                  <ChildrenSelector
                    value={numberOfChildren}
                    onChange={setNumberOfChildren}
                    max={parseInt(numberOfPeople) - 1}
                  />
                  
                  {numberOfChildren > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Sur {numberOfPeople} personne{parseInt(numberOfPeople) > 1 ? 's' : ''}, {parseInt(numberOfPeople) - numberOfChildren} adulte{(parseInt(numberOfPeople) - numberOfChildren) > 1 ? 's' : ''} et {numberOfChildren} enfant{numberOfChildren > 1 ? 's' : ''}
                    </p>
                  )}

                  {reservationSettings?.childrenHelpText && (
                    <p className="text-xs text-muted-foreground italic">
                      {reservationSettings.childrenHelpText}
                    </p>
                  )}
                </div>
              )}

              {/* Stroller checkbox - independent from children */}
              {strollerEnabled && numberOfPeople && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="stroller-availability"
                      checked={hasStroller}
                      onCheckedChange={(checked) => setHasStroller(checked === true)}
                    />
                    <label
                      htmlFor="stroller-availability"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5"
                    >
                      <Baby className="w-4 h-4" />
                      Avec poussette
                    </label>
                  </div>
                  {reservationSettings?.strollerHelpText && (
                    <p className="text-xs text-muted-foreground italic ml-6">
                      {reservationSettings.strollerHelpText}
                    </p>
                  )}
                </div>
              )}

              {/* Terrace Preference Selector */}
              {reservationSettings?.terraceEnabled && selectedDate && (
                <div className="space-y-3">
                  <TerracePreferenceSelector
                    value={terracePreference}
                    onChange={setTerracePreference}
                    helpText={reservationSettings?.terraceHelpText}
                    compact
                  />
                </div>
              )}

              {/* Time Slots / Times */}
              {reservationSettings && selectedDate && (isTimeMode ? availableBookingTimes.length > 0 : Object.keys(timeSlotAvailability).length > 0) && (
                <div className="space-y-3">
                  <Label className="text-base font-semibold">
                    {isTimeMode 
                      ? `${reservationSettings?.terraceEnabled ? '4' : '3'}. Sélectionnez une heure *` 
                      : `${reservationSettings?.terraceEnabled ? '4' : '3'}. Sélectionnez un créneau horaire *`}
                  </Label>
                  
                  {isTimeMode ? (
                    // Mode TIME : afficher la grille d'heures individuelles
                    <TimeBookingGrid
                      availableTimes={timeAvailability}
                      selectedTime={selectedTimeSlot}
                      onTimeSelect={setSelectedTimeSlot}
                    />
                  ) : (
                    // Mode SLOT : afficher les cartes de créneaux
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                      {timeSlots.map((slot) => {
                        const slotInfo = timeSlotAvailability[slot.start];
                        const isAvailable = slotInfo && slotInfo.available > 0;
                        const isSelected = selectedTimeSlot === slot.start;

                        return (
                          <Card
                            key={slot.start}
                            className={cn(
                              "cursor-pointer transition-all hover:shadow-md active:scale-95",
                              isSelected && "ring-2 ring-primary",
                              !isAvailable && "opacity-50 cursor-not-allowed"
                            )}
                            onClick={() => isAvailable && setSelectedTimeSlot(slot.start)}
                          >
                            <CardContent className="p-3 sm:p-4">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <Clock className={cn(
                                    "w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0",
                                    isAvailable ? "text-primary" : "text-muted-foreground"
                                  )} />
                                   <div className="min-w-0">
                                     <p className="font-semibold text-sm sm:text-base truncate">{slot.label}</p>
                                     {slotInfo && (
                                       <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
                                         {slotInfo.available} table{slotInfo.available > 1 ? 's' : ''}
                                       </p>
                                     )}
                                   </div>
                                </div>
                                <Badge
                                  variant={isAvailable ? "default" : "secondary"}
                                  className={cn(
                                    "text-xs whitespace-nowrap self-start sm:self-center",
                                    isAvailable && "bg-green-500"
                                  )}
                                >
                                  {isAvailable ? "Dispo" : "Complet"}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Reset Button */}
              {(selectedDate || selectedTimeSlot || numberOfPeople) && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="w-full bg-accent/10 border-accent hover:bg-accent/20 active:scale-95 transition-all"
                >
                  Réinitialiser la recherche
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Search Results */}
        {showResults && (
          <div className="space-y-6">
            {/* Table recommandée */}
            {optimalSuggestion && (
              <Card className="border-2 border-green-500 bg-green-50 dark:bg-green-950/20">
                <CardContent className="pt-4 sm:pt-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-green-500 rounded-full flex-shrink-0">
                      <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-green-900 dark:text-green-100 mb-1 sm:mb-2">
                        Table recommandée
                      </h3>
                      <p className="text-xs sm:text-sm text-green-800 dark:text-green-200 mb-3 sm:mb-4">
                        <span className="font-medium text-sm sm:text-base">{optimalSuggestion.name}</span> • {optimalSuggestion.capacity} pers.
                        <br className="hidden sm:inline" />
                        <span className="text-xs block sm:inline sm:ml-1">
                          {optimalSuggestion.capacity === parseInt(numberOfPeople || "0") ? "Capacité exacte" :
                           optimalSuggestion.capacity === parseInt(numberOfPeople || "0") + 1 ? "Très proche" : 
                           "Meilleure option"}
                        </span>
                      </p>
                      <Button
                        onClick={() => {
                          if (!numberOfPeople) {
                            toast({
                              title: "Nombre de personnes requis",
                              description: "Veuillez sélectionner le nombre de personnes avant de créer une réservation.",
                              variant: "destructive",
                            });
                            return;
                          }
                          
                          if (optimalSuggestion.type === 'combined') {
                            const combinedTable = combinedTables.find(ct => ct.id === optimalSuggestion.id);
                            if (combinedTable) {
                              setSelectedCombinedTableForReservation(combinedTable);
                              setSelectedTableForReservation(null);
                              setSelectedPersonCountForReservation(parseInt(numberOfPeople));
                            }
                          } else {
                            const table = searchResults.available.find(t => t.id === optimalSuggestion.id);
                            if (table) {
                              setSelectedTableForReservation(table);
                              setSelectedCombinedTableForReservation(null);
                              setSelectedPersonCountForReservation(parseInt(numberOfPeople));
                            }
                          }
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white active:scale-95 transition-transform text-sm sm:text-base"
                        size="sm"
                      >
                        Réserver cette table
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card>
              <CardContent className="pt-4 sm:pt-6">
                <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">
                      {searchResults.available.length + searchResults.availableCombined.length}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      <span className="hidden sm:inline">Table{(searchResults.available.length + searchResults.availableCombined.length) > 1 ? 's' : ''} disponible{(searchResults.available.length + searchResults.availableCombined.length) > 1 ? 's' : ''}</span>
                      <span className="sm:hidden">Dispo</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold text-red-600 dark:text-red-400">
                      {searchResults.unavailable.length + searchResults.unavailableCombined.length}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      <span className="hidden sm:inline">Table{(searchResults.unavailable.length + searchResults.unavailableCombined.length) > 1 ? 's' : ''} indisponible{(searchResults.unavailable.length + searchResults.unavailableCombined.length) > 1 ? 's' : ''}</span>
                      <span className="sm:hidden">Indispo</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold text-primary">
                      {searchResults.totalCapacity}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      <span className="hidden sm:inline">Places totales</span>
                      <span className="sm:hidden">Places</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Conditional limit info badge (staff informational, no blocking) */}
            {effectiveLimit && (
              <Alert className="border-accent">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <span className="font-medium">Limite active :</span>{' '}
                  {effectiveLimit.type === 'reservations'
                    ? `max ${effectiveLimit.value} réservation${effectiveLimit.value > 1 ? 's' : ''}`
                    : `max ${effectiveLimit.value} personne${effectiveLimit.value > 1 ? 's' : ''}`}
                  {' '}
                  <Badge variant="outline" className="ml-1 text-xs">
                    {effectiveLimit.source === 'specific_time' ? 'Heure précise' : effectiveLimit.source === 'specific' ? 'Jour + créneau' : effectiveLimit.source === 'day' ? 'Jour' : 'Globale'}
                  </Badge>
                  <span className="text-muted-foreground ml-2 text-xs">(bypass staff — non bloquant)</span>
                </AlertDescription>
              </Alert>
            )}


            {searchResults.available.length > 0 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h3 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                    {optimalSuggestion ? "Autres tables disponibles" : "Tables disponibles"}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm text-muted-foreground">Trier par :</span>
                    <Button
                      variant={sortField === 'name' ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => {
                        if (sortField === 'name') {
                          setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('name');
                          setSortDirection('asc');
                        }
                      }}
                      className="h-8"
                    >
                      Nom
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />
                      )}
                    </Button>
                    <Button
                      variant={sortField === 'capacity' ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => {
                        if (sortField === 'capacity') {
                          setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('capacity');
                          setSortDirection('asc');
                        }
                      }}
                      className="h-8"
                    >
                      Capacité
                      {sortField === 'capacity' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {(() => {
                    const personsNum = numberOfPeople ? parseInt(numberOfPeople) : 0;
                    const adultsNum = childrenEnabled ? Math.max(0, personsNum - numberOfChildren) : personsNum;
                    const effectiveCap = hasChildren
                      ? calculateEffectiveCapacity(adultsNum, numberOfChildren, childrenCapacityMode as 'full' | 'half' | 'none')
                      : personsNum;

                    return sortedAvailable
                      .map((table) => {
                      const isUnderCapacity = personsNum > 0 && table.capacity < effectiveCap;
                      return (
                        <Card key={table.id} className={cn("hover:shadow-lg transition-shadow", isUnderCapacity && "border-amber-400 dark:border-amber-600")}>
                          <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
                            <CardTitle className="text-sm sm:text-lg flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <span className="truncate">{table.name}</span>
                              {isUnderCapacity && (
                                <Badge className="bg-amber-500 text-white text-xs self-start sm:self-auto whitespace-nowrap">
                                  Sous-capacité
                                </Badge>
                              )}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 sm:p-4 pt-0 space-y-2 sm:space-y-3">
                            <div className="flex items-center text-muted-foreground text-xs sm:text-sm">
                              <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                              <span>{table.capacity} pl.</span>
                            </div>
                            {isUnderCapacity && (
                              <p className="text-xs text-amber-600 dark:text-amber-400">
                                Capacité {table.capacity} — demandé {effectiveCap}
                              </p>
                            )}
                            <Button 
                              className="w-full active:scale-95 transition-transform" 
                              size="sm"
                              onClick={() => {
                                if (!numberOfPeople) {
                                  toast({
                                    title: "Nombre de personnes requis",
                                    description: "Veuillez sélectionner le nombre de personnes avant de créer une réservation.",
                                    variant: "destructive",
                                  });
                                  return;
                                }
                                
                                setSelectedTableForReservation(table);
                                setSelectedCombinedTableForReservation(null);
                                setSelectedPersonCountForReservation(parseInt(numberOfPeople));
                              }}
                            >
                              <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                              <span className="hidden sm:inline">Créer une réservation</span>
                              <span className="sm:hidden">Réserver</span>
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Available Combined Tables */}
            {searchResults.availableCombined.length > 0 && (
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                  <span className="hidden sm:inline">Tables combinées disponibles</span>
                  <span className="sm:hidden">Combinées dispo</span>
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {(() => {
                    const personsNum = numberOfPeople ? parseInt(numberOfPeople) : 0;
                    const adultsNum = childrenEnabled ? Math.max(0, personsNum - numberOfChildren) : personsNum;
                    const effectiveCap = hasChildren
                      ? calculateEffectiveCapacity(adultsNum, numberOfChildren, childrenCapacityMode as 'full' | 'half' | 'none')
                      : personsNum;

                    return sortedAvailableCombined.map((combinedTable) => {
                      const isUnderCapacity = personsNum > 0 && combinedTable.totalCapacity < effectiveCap;
                      return (
                        <Card key={combinedTable.id} className={cn("hover:shadow-lg transition-shadow border-2", isUnderCapacity ? "border-amber-400 dark:border-amber-600" : "border-primary/20")}>
                          <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
                            <CardTitle className="text-sm sm:text-lg flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <span className="truncate">{combinedTable.name}</span>
                              <Badge variant="secondary" className="text-xs self-start sm:self-auto whitespace-nowrap">Combinée</Badge>
                              {isUnderCapacity && (
                                <Badge className="bg-amber-500 text-white text-xs self-start sm:self-auto whitespace-nowrap">
                                  Sous-capacité
                                </Badge>
                              )}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 sm:p-4 pt-0 space-y-2 sm:space-y-3">
                            <div className="flex items-center text-muted-foreground text-xs sm:text-sm">
                              <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                              <span>{combinedTable.totalCapacity} pl.</span>
                            </div>
                            {isUnderCapacity && (
                              <p className="text-xs text-amber-600 dark:text-amber-400">
                                Capacité {combinedTable.totalCapacity} — demandé {effectiveCap}
                              </p>
                            )}
                            <Button 
                              className="w-full active:scale-95 transition-transform" 
                              size="sm"
                              onClick={() => {
                                if (!numberOfPeople) {
                                  toast({
                                    title: "Nombre de personnes requis",
                                    description: "Veuillez sélectionner le nombre de personnes avant de créer une réservation.",
                                    variant: "destructive",
                                  });
                                  return;
                                }
                                
                                setSelectedCombinedTableForReservation(combinedTable);
                                setSelectedTableForReservation(null);
                                setSelectedPersonCountForReservation(parseInt(numberOfPeople));
                              }}
                            >
                              <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                              <span className="hidden sm:inline">Créer une réservation</span>
                              <span className="sm:hidden">Réserver</span>
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Manual-Only Tables (non-bookable by system) */}
            {(searchResults.manualOnly.length > 0 || searchResults.manualOnlyCombined.length > 0) && (
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 dark:text-amber-400" />
                  <span className="hidden sm:inline">Tables hors réservation</span>
                  <span className="sm:hidden">Hors résa</span>
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Ces tables ne sont pas assignées automatiquement par le système. Vous pouvez les réserver manuellement.
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {searchResults.manualOnly.map(({ table, reason }) => {
                    const isPhysicallyAvailable = reason === null;
                    return (
                      <Card key={table.id} className={cn(
                        "border-dashed border-2",
                        isPhysicallyAvailable 
                          ? "border-amber-400 dark:border-amber-600" 
                          : "border-muted opacity-60"
                      )}>
                        <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
                          <CardTitle className="text-sm sm:text-lg flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <span className="truncate">{table.name}</span>
                            <Badge variant="outline" className="text-xs self-start sm:self-auto whitespace-nowrap border-amber-500 text-amber-700 dark:text-amber-400">
                              Hors résa
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 sm:p-4 pt-0 space-y-2 sm:space-y-3">
                          <div className="flex items-center text-muted-foreground text-xs sm:text-sm">
                            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                            <span>{table.capacity} pl.</span>
                          </div>
                          {reason && (
                            <Alert variant="destructive" className="py-1.5 sm:py-2">
                              <AlertDescription className="text-xs">
                                {reason}
                              </AlertDescription>
                            </Alert>
                          )}
                          {isPhysicallyAvailable && (
                            <Button 
                              variant="outline"
                              className="w-full active:scale-95 transition-transform border-amber-500 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950" 
                              size="sm"
                              onClick={() => {
                                if (!numberOfPeople) {
                                  toast({
                                    title: "Nombre de personnes requis",
                                    description: "Veuillez sélectionner le nombre de personnes avant de créer une réservation.",
                                    variant: "destructive",
                                  });
                                  return;
                                }
                                
                                setSelectedTableForReservation(table);
                                setSelectedCombinedTableForReservation(null);
                                setSelectedPersonCountForReservation(parseInt(numberOfPeople));
                              }}
                            >
                              <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                              <span className="hidden sm:inline">Réserver manuellement</span>
                              <span className="sm:hidden">Réserver</span>
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  {searchResults.manualOnlyCombined.map(({ combinedTable: ct, reason }) => {
                    const isPhysicallyAvailable = reason === null;
                    return (
                      <Card key={ct.id} className={cn(
                        "border-dashed border-2",
                        isPhysicallyAvailable 
                          ? "border-amber-400 dark:border-amber-600" 
                          : "border-muted opacity-60"
                      )}>
                        <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
                          <CardTitle className="text-sm sm:text-lg flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <span className="truncate">{ct.name}</span>
                            <div className="flex flex-wrap gap-1">
                              <Badge variant="outline" className="text-xs self-start sm:self-auto whitespace-nowrap border-amber-500 text-amber-700 dark:text-amber-400">
                                Hors résa
                              </Badge>
                              <Badge variant="secondary" className="text-xs self-start sm:self-auto whitespace-nowrap">
                                Combinée
                              </Badge>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 sm:p-4 pt-0 space-y-2 sm:space-y-3">
                          <div className="flex items-center text-muted-foreground text-xs sm:text-sm">
                            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                            <span>{ct.totalCapacity} pl.</span>
                          </div>
                          {reason && (
                            <Alert variant="destructive" className="py-1.5 sm:py-2">
                              <AlertDescription className="text-xs">
                                {reason}
                              </AlertDescription>
                            </Alert>
                          )}
                          {isPhysicallyAvailable && (
                            <Button 
                              variant="outline"
                              className="w-full active:scale-95 transition-transform border-amber-500 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950" 
                              size="sm"
                              onClick={() => {
                                if (!numberOfPeople) {
                                  toast({
                                    title: "Nombre de personnes requis",
                                    description: "Veuillez sélectionner le nombre de personnes avant de créer une réservation.",
                                    variant: "destructive",
                                  });
                                  return;
                                }
                                setSelectedTableForReservation(null);
                                setSelectedCombinedTableForReservation(ct);
                                setSelectedPersonCountForReservation(parseInt(numberOfPeople));
                              }}
                            >
                              <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                              <span className="hidden sm:inline">Réserver manuellement</span>
                              <span className="sm:hidden">Réserver</span>
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {searchResults.unavailable.length > 0 && (
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                  <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 dark:text-red-400" />
                  Tables indisponibles
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {searchResults.unavailable.map(({ table, reason }) => (
                    <Card key={table.id} className="opacity-60">
                      <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
                        <CardTitle className="text-sm sm:text-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                          <span className="truncate">{table.name}</span>
                          <Badge variant="destructive" className="text-xs self-start sm:self-auto whitespace-nowrap">
                            Indispo
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-4 pt-0 space-y-2 sm:space-y-3">
                        <div className="flex items-center text-muted-foreground text-xs sm:text-sm">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                          <span>{table.capacity} pl.</span>
                        </div>
                        <Alert variant="destructive" className="py-1.5 sm:py-2">
                          <AlertDescription className="text-xs">
                            {reason}
                          </AlertDescription>
                        </Alert>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Unavailable Combined Tables */}
            {searchResults.unavailableCombined.length > 0 && (
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                  <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 dark:text-red-400" />
                  <span className="hidden sm:inline">Tables combinées indisponibles</span>
                  <span className="sm:hidden">Combinées indispo</span>
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {searchResults.unavailableCombined.map(({ combinedTable, reason }) => (
                    <Card key={combinedTable.id} className="opacity-60">
                      <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
                        <CardTitle className="text-sm sm:text-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                          <span className="truncate">{combinedTable.name}</span>
                          <Badge variant="destructive" className="text-xs self-start sm:self-auto whitespace-nowrap">
                            Indispo
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-4 pt-0 space-y-2 sm:space-y-3">
                        <div className="flex items-center text-muted-foreground text-xs sm:text-sm">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                          <span>{combinedTable.totalCapacity} pl.</span>
                        </div>
                        <Alert variant="destructive" className="py-1.5 sm:py-2">
                          <AlertDescription className="text-xs">
                            {reason}
                          </AlertDescription>
                        </Alert>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {searchResults.available.length === 0 && searchResults.unavailable.length === 0 && 
             searchResults.availableCombined.length === 0 && searchResults.unavailableCombined.length === 0 && (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className="text-muted-foreground space-y-3">
                    <Clock className="w-16 h-16 mx-auto opacity-50" />
                    <p className="text-lg">Aucune table trouvée pour ces critères.</p>
                    <p className="text-sm">Essayez de modifier votre recherche.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* New Reservation Dialog */}
      {selectedTableForReservation && selectedDate && selectedTimeSlot && (
        <NewReservationDialog
          open={!!selectedTableForReservation}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTableForReservation(null);
              setSelectedPersonCountForReservation(undefined);
            }
          }}
          timeSlot={selectedTimeSlot}
          date={selectedDate}
          availableTables={[selectedTableForReservation]}
          combinedTables={combinedTables}
          allReservations={supabaseReservations}
          existingClients={existingClients}
          availabilities={availabilities}
          weekdayAvailabilities={weekdayAvailabilities}
          onReservationCreate={handleCreateReservation}
          readOnlyMode={true}
          preselectedTable={selectedTableForReservation}
          preselectedPersonCount={selectedPersonCountForReservation}
          preselectedChildren={numberOfChildren}
          preselectedStroller={hasStroller}
          establishmentId={establishmentId || undefined}
          maxPeople={reservationSettings?.maxPeople}
          childrenEnabled={reservationSettings?.childrenEnabled}
          strollerEnabled={reservationSettings?.strollerEnabled}
          childrenCapacityMode={reservationSettings?.childrenCapacityMode}
          terraceEnabled={reservationSettings?.terraceEnabled}
          preselectedTerracePreference={terracePreference}
          bookablePeriods={bookablePeriods}
          reservationSettings={reservationSettings ? {
            bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
            reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
            bookingIntervalMinutes: reservationSettings.bookingIntervalMinutes as 15 | 30 | 60,
          } : undefined}
        />
      )}

      {/* New Reservation Dialog for Combined Tables */}
      {selectedCombinedTableForReservation && selectedDate && selectedTimeSlot && (
        <NewReservationDialog
          open={!!selectedCombinedTableForReservation}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedCombinedTableForReservation(null);
              setSelectedPersonCountForReservation(undefined);
            }
          }}
          timeSlot={selectedTimeSlot}
          date={selectedDate}
          availableTables={searchResults.available}
          combinedTables={combinedTables}
          allReservations={supabaseReservations}
          existingClients={existingClients}
          availabilities={availabilities}
          weekdayAvailabilities={weekdayAvailabilities}
          onReservationCreate={handleCreateReservation}
          preselectedCombinedTable={selectedCombinedTableForReservation}
          readOnlyMode={true}
          preselectedPersonCount={selectedPersonCountForReservation}
          preselectedChildren={numberOfChildren}
          preselectedStroller={hasStroller}
          establishmentId={establishmentId || undefined}
          maxPeople={reservationSettings?.maxPeople}
          childrenEnabled={reservationSettings?.childrenEnabled}
          strollerEnabled={reservationSettings?.strollerEnabled}
          childrenCapacityMode={reservationSettings?.childrenCapacityMode}
          terraceEnabled={reservationSettings?.terraceEnabled}
          preselectedTerracePreference={terracePreference}
          bookablePeriods={bookablePeriods}
          reservationSettings={reservationSettings ? {
            bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
            reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
            bookingIntervalMinutes: reservationSettings.bookingIntervalMinutes as 15 | 30 | 60,
          } : undefined}
        />
      )}

      {/* Retroactive Reservation Dialog (manager) — mode complet, toggle pré-activé */}
      <NewReservationDialog
        open={retroactiveDialogOpen}
        onOpenChange={setRetroactiveDialogOpen}
        timeSlot=""
        date={new Date(Date.now() - 24 * 60 * 60 * 1000)}
        availableTables={tables}
        combinedTables={combinedTables}
        allReservations={supabaseReservations}
        existingClients={existingClients}
        availabilities={availabilities}
        weekdayAvailabilities={weekdayAvailabilities}
        onReservationCreate={handleCreateReservation}
        readOnlyMode={false}
        defaultRetroactive={true}
        establishmentId={establishmentId || undefined}
        maxPeople={reservationSettings?.maxPeople}
        childrenEnabled={reservationSettings?.childrenEnabled}
        strollerEnabled={reservationSettings?.strollerEnabled}
        childrenCapacityMode={reservationSettings?.childrenCapacityMode}
        terraceEnabled={reservationSettings?.terraceEnabled}
        bookablePeriods={bookablePeriods}
        reservationSettings={reservationSettings ? {
          bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
          reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
          bookingIntervalMinutes: reservationSettings.bookingIntervalMinutes as 15 | 30 | 60,
        } : undefined}
      />
    </div>
  );
};

export default Availability;
